import { FC, useCallback, useEffect, useState } from 'react';
import { CloseRounded, OpenInNewRounded } from '@mui/icons-material';
import { Button, Dialog, IconButton, Typography } from '@mui/material';
import type { FirmDocumentFileActionReference } from 'pages/Documents/FirmDocuments/components/FirmDocumentFileActions';
import FirmDocumentPreviewPanel from 'pages/Documents/FirmDocuments/components/FirmDocumentPreviewPanel';
import MeasurementDateDocumentsTree from 'pages/Documents/FirmDocuments/components/MeasurementDateDocumentsTree';
import { getFirmDocumentDownloadTarget } from 'pages/Documents/FirmDocuments/utilities';
import {
  useFirmDocumentActions,
  useFirmDocumentMeasurementDateDocumentsQuery,
} from 'services/hooks/documents';
import { PrismThemeProvider } from 'theme';
import { BrowseDocumentsModalProps } from './BrowseDocumentsModal.props';
import {
  BrowseDocumentsBody,
  BrowseDocumentsDialogContent,
  BrowseDocumentsDialogTitle,
  BrowseDocumentsEmptyPreview,
  BrowseDocumentsListPane,
  BrowseDocumentsPreviewPane,
  BrowseDocumentsSubtitle,
  BrowseDocumentsTitleText,
} from './BrowseDocumentsModal.styles';

const BrowseDocumentsModal: FC<BrowseDocumentsModalProps> = ({
  open,
  onClose,
  companyId,
  companyName,
  firmId,
  measurementDateId,
  measurementDateLabel,
  informationPageUrl,
}) => {
  const [selectedDocumentReference, setSelectedDocumentReference] =
    useState<FirmDocumentFileActionReference | null>(null);
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const [downloadingDocumentReferenceId, setDownloadingDocumentReferenceId] = useState<number | null>(null);

  const { downloadDocuments } = useFirmDocumentActions();
  const { data, isError, isLoading } = useFirmDocumentMeasurementDateDocumentsQuery({
    companyId: companyId ?? null,
    enabled: open,
    measurementDateId: measurementDateId ?? null,
  });

  useEffect(() => {
    if (!open) {
      setSelectedDocumentReference(null);
      setIsPreviewExpanded(false);
      setDownloadingDocumentReferenceId(null);
    }
  }, [open]);

  const handleDocumentPreviewSelect = useCallback((documentReference: FirmDocumentFileActionReference) => {
    setSelectedDocumentReference(documentReference);
  }, []);

  const handleDocumentDownload = useCallback(
    (documentReference: FirmDocumentFileActionReference) => {
      const downloadTarget = getFirmDocumentDownloadTarget(documentReference, firmId);

      if (!downloadTarget) {
        return;
      }

      const documentReferenceId = documentReference.document_reference_id;
      setDownloadingDocumentReferenceId(documentReferenceId);
      downloadDocuments({ documents: [downloadTarget] })
        .catch(() => undefined)
        .finally(() => {
          setDownloadingDocumentReferenceId(currentDocumentReferenceId =>
            currentDocumentReferenceId === documentReferenceId ? null : currentDocumentReferenceId
          );
        });
    },
    [downloadDocuments, firmId]
  );

  const handlePreviewClose = useCallback(() => {
    setSelectedDocumentReference(null);
    setIsPreviewExpanded(false);
  }, []);

  const handlePreviewExpandToggle = useCallback(() => {
    setIsPreviewExpanded(current => !current);
  }, []);

  const handleOpenInformationPage = useCallback(() => {
    if (!informationPageUrl) {
      return;
    }

    window.open(informationPageUrl, '_blank', 'noopener,noreferrer');
  }, [informationPageUrl]);

  const title = companyName ? `${companyName} documents` : 'Browse documents';
  const subtitle = measurementDateLabel ? `Measurement date: ${measurementDateLabel}` : null;

  return (
    <PrismThemeProvider>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        id="browse-documents-modal"
        data-testid="browse-documents-modal">
        <BrowseDocumentsDialogTitle>
          <BrowseDocumentsTitleText>
            <Typography variant="h6" component="span" noWrap>
              {title}
            </Typography>
            {subtitle ? (
              <BrowseDocumentsSubtitle variant="body2" noWrap>
                {subtitle}
              </BrowseDocumentsSubtitle>
            ) : null}
          </BrowseDocumentsTitleText>
          {informationPageUrl ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewRounded />}
              onClick={handleOpenInformationPage}
              id="browse-documents-open-information-page"
              data-testid="browse-documents-open-information-page">
              Open Information page
            </Button>
          ) : null}
          <IconButton
            aria-label="Close browse documents"
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
            id="browse-documents-modal-close"
            data-testid="browse-documents-modal-close">
            <CloseRounded />
          </IconButton>
        </BrowseDocumentsDialogTitle>
        <BrowseDocumentsDialogContent dividers>
          <BrowseDocumentsBody>
            {!isPreviewExpanded ? (
              <BrowseDocumentsListPane data-testid="browse-documents-list-pane">
                <MeasurementDateDocumentsTree
                  documents={data}
                  isLoading={isLoading}
                  isError={isError}
                  canDownloadDocuments
                  canManageDocuments={false}
                  canSelectDocuments={false}
                  canCopyDocuments={false}
                  canMoveDocuments={false}
                  showEmptyFolders={false}
                  activeDocumentReferenceId={selectedDocumentReference?.document_reference_id ?? null}
                  downloadingDocumentReferenceId={downloadingDocumentReferenceId}
                  onDocumentPreviewSelect={handleDocumentPreviewSelect}
                  onDocumentDownload={handleDocumentDownload}
                  emptyTitle="No documents uploaded"
                  emptyDescription="Documents uploaded for this measurement date will appear here."
                />
              </BrowseDocumentsListPane>
            ) : null}
            <BrowseDocumentsPreviewPane
              isExpanded={isPreviewExpanded}
              data-testid="browse-documents-preview-pane">
              {selectedDocumentReference ? (
                <FirmDocumentPreviewPanel
                  canCopyDocuments={false}
                  canDeleteDocuments={false}
                  canDownloadDocuments
                  canRenameDocuments={false}
                  documentReference={selectedDocumentReference}
                  downloadingDocumentReferenceId={downloadingDocumentReferenceId}
                  firmId={firmId}
                  isExpanded={isPreviewExpanded}
                  onDocumentDownload={handleDocumentDownload}
                  onPreviewExpandToggle={handlePreviewExpandToggle}
                  onPreviewClose={handlePreviewClose}
                />
              ) : (
                <BrowseDocumentsEmptyPreview data-testid="browse-documents-empty-preview">
                  <Typography variant="body2">Select a document to preview it here.</Typography>
                </BrowseDocumentsEmptyPreview>
              )}
            </BrowseDocumentsPreviewPane>
          </BrowseDocumentsBody>
        </BrowseDocumentsDialogContent>
      </Dialog>
    </PrismThemeProvider>
  );
};

export default BrowseDocumentsModal;
